import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { API } from "../../../api";
import { Job } from "../../../models";
import { UserContext } from "../../../hooks/UserContext";
import { FileDownloader } from "../../../utilities/downloadFile";
import { IAirtableAttachment } from "../../../utilities/types";
import { GeoJSON } from "../../../utilities/utils";
import type { Mission, IJob, IDeal, MissionCSV } from "@rogoag/shared-ts";

const ATTACHMENT_LABELS: Record<string, string> = {
	pointsSHP: "Points SHP",
	boundarySHP: "Boundary SHP",
	boundaryGeoJSON: "Boundary GeoJSON",
	boundaryGeoJson: "Boundary GeoJSON",
	zonesSHP: "Zones SHP",
	executedPointsSHP: "Executed Points SHP",
	executedBoundarySHP: "Executed Boundary SHP",
	executedPointsBoundarySHP: "Executed Pts+Bnd SHP",
	labPDF: "Lab PDF",
	labCSV: "Lab CSV",
	labResults: "Lab Results",
	fieldNotes: "Field Notes",
	exeMission: "Executed Mission",
	intMission: "Intended Mission",
};

export interface AvailableAttachment {
	key: string;
	label: string;
	attachments: IAirtableAttachment[];
}

export const usePostSamplingIssuesViewModel = () => {
	const currentUser = useContext(UserContext);
	const isDeveloper = currentUser?.isDeveloper ?? false;

	const [jobs, setJobs] = useState<Job[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fixingIds, setFixingIds] = useState<Set<string>>(new Set());
	const [fixingAll, setFixingAll] = useState(false);
	const [countdown, setCountdown] = useState<number | null>(null);
	const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);

	const [selectedJob, setSelectedJob] = useState<Job | null>(null);
	const [mapLoading, setMapLoading] = useState(false);
	const [exeMission, setExeMission] = useState<Mission | null>(null);
	const [intMission, setIntMission] = useState<Mission | null>(null);
	const [missionsLoading, setMissionsLoading] = useState(false);
	const [mapTab, setMapTab] = useState<"geodata" | "exe" | "int">("exe");
	const [csvDryRunData, setCsvDryRunData] = useState<MissionCSV[] | null>(null);
	const [csvDryRunModalOpen, setCsvDryRunModalOpen] = useState(false);
	const [fudgingCoreId, setFudgingCoreId] = useState<string | null>(null);

	useEffect(() => {
		return () => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
		};
	}, []);

	const sleepWithCountdown = useCallback((seconds: number) => {
		return new Promise<void>((resolve) => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
			setCountdown(seconds);
			countdownRef.current = setInterval(() => {
				setCountdown((prev) => {
					if (prev !== null && prev <= 1) {
						clearInterval(countdownRef.current);
						resolve();
						return null;
					}
					return prev !== null ? prev - 1 : null;
				});
			}, 1000);
		});
	}, []);

	useEffect(() => {
		if (isDeveloper) {
			fetchJobs();
		}
	}, [isDeveloper]);

	const fetchJobs = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await API.Data.Jobs.issues.getPostSamplingStuckJobs();
			setJobs(result);
		} catch (err: any) {
			setError(`Failed to load post-sampling issues: ${err.message || "Unknown error"}`);
		} finally {
			setLoading(false);
		}
	};

	const selectJob = useCallback(async (job: Job) => {
		setSelectedJob(job);
		setExeMission(null);
		setIntMission(null);
		setCsvDryRunData(null);

		// Load boundary, points, and zones GeoJSON for JobMap
		setMapLoading(true);
		try {
			const [boundary, points, zones] = await Promise.all([
				GeoJSON.getBoundary(job),
				GeoJSON.getPoints(job),
				GeoJSON.getZones(job),
			]);

			if (boundary || points || zones) {
				const updatedJob = {
					...job,
					files: {
						...job.files,
						boundaryGeoJSON: boundary ?? undefined,
						pointsGeoJSON: points ?? undefined,
						zonesGeoJSON: zones ?? undefined,
					},
				};
				setSelectedJob(updatedJob);
			}
		} catch (err) {
			console.warn("Failed to load GeoJSON for map:", err);
		} finally {
			setMapLoading(false);
		}

		// Load missions in parallel
		setMissionsLoading(true);
		try {
			const [exe, int] = await Promise.all([job.exeMission(), job.intMission()]);
			setExeMission(exe ?? null);
			setIntMission(int ?? null);

			// Generate CSV dry-run data if executed mission is available
			if (exe) {
				try {
					const deals = job.dealIds.length > 0 ? await API.Data.Deals.get.byIds(job.dealIds) : [];
					const deal = deals[0];

					const iJob: IJob = {
						id: job.id,
						name: job.fieldName,
						sampleDateManOverrideDate: job.dateSampledManualOverride
							? String(job.dateSampledManualOverride)
							: undefined,
						jobFlags: job.jobFlags,
						eventId: job.eventId,
						grower: [job.growerName],
						finalLabAccNumber: job.labAccountNumber ? [job.labAccountNumber] : undefined,
						labInstructions: job.labInstructions,
					};

					const iDeal: IDeal = deal
						? { id: deal.id, fieldParameters: deal.fieldParameters, samplingTypeSpecial: deal.samplingTypeSpecial }
						: { id: "" };

					setCsvDryRunData(exe.generateLabCSVs(iJob, iDeal));
				} catch (err) {
					console.warn("Failed to generate CSV dry-run:", err);
				}
			}
		} catch (err) {
			console.warn("Failed to load missions:", err);
		} finally {
			setMissionsLoading(false);
		}
	}, []);

	const getAvailableAttachments = useCallback((job: Job): AvailableAttachment[] => {
		if (!job.attachments) return [];
		const result: AvailableAttachment[] = [];
		for (const [key, label] of Object.entries(ATTACHMENT_LABELS)) {
			const attachments = job.attachments[key as keyof typeof job.attachments] as IAirtableAttachment[] | undefined;
			if (attachments && attachments.length > 0) {
				result.push({ key, label, attachments });
			}
		}
		return result;
	}, []);

	const downloadAttachment = useCallback(async (attachment: AvailableAttachment, jobId: string) => {
		if (attachment.attachments.length === 1) {
			await FileDownloader.downloadAttachment(
				attachment.attachments[0],
				attachment.attachments[0].filename,
				false,
				jobId,
			);
		} else {
			await FileDownloader.downloadAttachments(
				attachment.attachments,
				attachment.attachments[0].filename,
				true,
				false,
				jobId,
			);
		}
	}, []);

	const fixOne = async (id: string) => {
		setFixingIds((prev) => new Set(prev).add(id));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([id]);
			await sleepWithCountdown(15);
			await fetchJobs();
		} catch (err: any) {
			setError(`Failed to fix job: ${err.message || "Unknown error"}`);
		} finally {
			setFixingIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
		}
	};

	const downloadCsvDryRun = useCallback(() => {
		if (!csvDryRunData) return;
		for (const csv of csvDryRunData) {
			const blob = new Blob([csv.toString()], { type: "text/csv" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = csv.filename;
			a.click();
			URL.revokeObjectURL(url);
		}
	}, [csvDryRunData]);

	const fudgeCore = async (jobId: string, coreId: string) => {
		setFudgingCoreId(coreId);
		setError(null);
		try {
			await API.Missions.fudgeCore(jobId, coreId);
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([jobId]);
			await new Promise((r) => setTimeout(r, 10_000));
			await fetchJobs();
			if (selectedJob) {
				await selectJob(selectedJob);
			}
		} catch (err: any) {
			setError(`Failed to fudge core: ${err.message || "Unknown error"}`);
		} finally {
			setFudgingCoreId(null);
		}
	};

	const deleteCore = async (jobId: string, coreId: string) => {
		setFudgingCoreId(coreId);
		setError(null);
		try {
			await API.Missions.deleteCore(jobId, coreId);
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([jobId]);
			await new Promise((r) => setTimeout(r, 15_000));
			await fetchJobs();
			if (selectedJob) {
				await selectJob(selectedJob);
			}
		} catch (err: any) {
			setError(`Failed to delete core: ${err.message || "Unknown error"}`);
		} finally {
			setFudgingCoreId(null);
		}
	};

	const deleteCores = async (jobId: string, coreIds: string[]) => {
		setFudgingCoreId("bulk");
		setError(null);
		try {
			await API.Missions.deleteCores(jobId, coreIds);
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([jobId]);
			await new Promise((r) => setTimeout(r, 15_000));
			await fetchJobs();
			if (selectedJob) {
				await selectJob(selectedJob);
			}
		} catch (err: any) {
			setError(`Failed to delete cores: ${err.message || "Unknown error"}`);
		} finally {
			setFudgingCoreId(null);
		}
	};

	const fixAll = async () => {
		const ids = jobs.map((j) => j.id);
		if (ids.length === 0) return;

		setFixingAll(true);
		setFixingIds(new Set(ids));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs(ids);
			await sleepWithCountdown(120);
			await fetchJobs();
		} catch (err: any) {
			setError(`Failed to fix all jobs: ${err.message || "Unknown error"}`);
		} finally {
			setFixingAll(false);
			setFixingIds(new Set());
		}
	};

	return {
		isDeveloper,
		jobs,
		loading,
		error,
		fixingIds,
		fixingAll,
		fixOne,
		fixAll,
		countdown,
		selectedJob,
		selectJob,
		mapLoading,
		exeMission,
		intMission,
		missionsLoading,
		mapTab,
		setMapTab,
		getAvailableAttachments,
		downloadAttachment,
		csvDryRunData,
		csvDryRunModalOpen,
		setCsvDryRunModalOpen,
		downloadCsvDryRun,
		fudgeCore,
		deleteCore,
		deleteCores,
		fudgingCoreId,
	};
};
